import { useEffect, useState } from 'react';

import { fetchEnrichments } from '../api';
import { config } from '../config';
import { synthesizeEnrichments } from '../synthesizer';

/**
 * Returns `{ [listingId]: Badge[] }` for the given listing ids.
 *
 * Backend path (default): when empavision is configured, fetches enrichments
 * from the host app's API proxy.
 *
 * Fallback path (opt-in): when the backend is disabled AND the caller passes
 * `{ listings, synthesize }`, badges are synthesized client-side from the
 * supplied `synthesize(listing) => Badge[]` rules. Same output shape, so
 * consumers don't change. Without `synthesize`, the disabled backend yields {}
 * (preserving the original behavior).
 *
 * @param {string[]} listingIds
 * @param {{ listings?: object[], synthesize?: (listing: object) => object[] }} [options]
 */
export const useEnrichments = (listingIds, options = {}) => {
  const { listings, synthesize } = options;
  const [enrichments, setEnrichments] = useState({});
  const ids = Array.isArray(listingIds) ? listingIds : [];
  const dependencyKey = ids.join(',');

  useEffect(() => {
    if (ids.length === 0) {
      setEnrichments({});
      return;
    }

    if (!config.isEnabled) {
      setEnrichments(synthesize ? synthesizeEnrichments(listings || [], synthesize) : {});
      return;
    }

    let isActive = true;

    fetchEnrichments(ids)
      .then(result => {
        if (isActive) {
          setEnrichments(result || {});
        }
      })
      .catch(() => {
        if (isActive) {
          setEnrichments({});
        }
      });

    return () => {
      isActive = false;
    };
  }, [dependencyKey]);

  return enrichments;
};
